import type { MockDbOptions } from "@/mock-api/mockDbVariants.ts";
import type { MockDbInitOptions } from "@/mock-api/msw/mswOptions.ts";
import { jsonLocalStorage } from "@/utils/jotai/atomStorage.ts";
import sleep from "@warrenio/utils/promise/sleep";
import type { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";
import invariant from "tiny-invariant";

/** Promise with the current MSW initialization progress. `undefined` if not initialized at all. */
let mswPromise: Promise<void> | undefined;

/** Cached value of whether MSW is enabled. `undefined` if not loaded yet. */
let isMswEnabled: boolean | undefined;

/** For use in stories and tests. */
let isForceEnabled = false;

const storage = jsonLocalStorage() as SyncStorage<boolean>;
const key = "mswEnabled";

/** MSW is not supported on production */
const isProd = import.meta.env.PROD;

/** Notify that something is waiting for our initialization (so we should immediately proceed with loading). */
let resolveWaitingForInitPromise: (() => void) | undefined;
const waitingForInitPromise = new Promise<void>((resolve) => {
    resolveWaitingForInitPromise = resolve;
});
const initDelayMs = 500;

export function isSupported() {
    return !isProd;
}

/**
 * NB: Guard this with the production check before, eg. `!import.meta.env.PROD && isMockApiEnabled()` to reduce bundle size in production.
 * Use the explicit `PROD` guard when the return value of this would flow into an optimizable condition (ie. dead code would be eliminated).
 * Note that some sites might already have `isProd` guards higher above them.
 */
export function isMockApiEnabled() {
    if (isProd) return false;

    if (isForceEnabled) return true;

    isMswEnabled ??= storage.getItem(key, false);
    return isMswEnabled;
}

export function setEnabled(enabled: boolean) {
    if (isProd) return;

    if (isForceEnabled) {
        console.warn("MSW is force enabled, setting should not be changed");
        return;
    }

    if (isMswEnabled === enabled) return;

    isMswEnabled = enabled;
    storage.setItem(key, enabled);

    // Re-initialize if we were already initialized, or start initialization if we are now enabled
    if (mswPromise != null || enabled) {
        void registerMsw(enabled);
    }
}

export function setForceEnabled() {
    if (!isForceEnabled) console.debug("Force enable MSW");

    isForceEnabled = true;
}

/** Wait for MSW initialization to finish (eg. before making API requests) */
export async function waitForMswInit() {
    if (isProd) return;

    if (!isMockApiEnabled()) return;

    resolveWaitingForInitPromise?.();

    invariant(mswPromise != null, "MSW must be initialized before using it");
    await mswPromise;
}

/** Lazily load MSW in the background. */
function beginMswInit(preInit?: () => Promise<void>) {
    if (isProd) return;

    if (!isMockApiEnabled()) return;

    void registerMsw(true, preInit);
}

/** Force MSW initialization. NB: Only use for tests. */
export async function forceMswInit() {
    await registerMsw(true);
}

/** Load MSW and initialize the mock DB. */
export function beginMockDbInit(options: MockDbInitOptions) {
    if (isProd) return;

    beginMswInit(async () => {
        const db = await import("@/mock-api/db.ts");
        db.initMockDb(options);
    });
}

export async function getMockDbOptions(): Promise<MockDbOptions> {
    if (isProd) return {};

    const db = await import("@/mock-api/db.ts");
    return db.getMockDb().options;
}

export async function resetMockDb(options: MockDbOptions) {
    if (isProd) return;

    const db = await import("@/mock-api/db.ts");
    db.resetDbWithOptions(options);
}

/**
 * Start the MSW initialization process.
 *
 * {@link mswPromise} is used as a lock to make sure that only one initialization is running at a time.
 */
function registerMsw(enabled: boolean, preInit?: () => Promise<void>): Promise<void> {
    const prevMswPromise = mswPromise;
    return (mswPromise = (async () => {
        // Wait for previous initialization to finish
        if (prevMswPromise) {
            console.debug("Waiting for previous MSW initialization to finish");
            await prevMswPromise;
        } else if (typeof process === "undefined" || !process?.versions?.node) {
            // Delay the initialization of MSW until some time has passed or the fetch client is waiting for it.
            // Initializing it early causes it to intercept static resource requests (eg. JS), which slows down the page load.

            // Do not delay in Node.js, otherwise it would slow down the tests.
            console.debug("Delaying MSW initialization for %s ms", initDelayMs);
            await Promise.race([sleep(initDelayMs), waitingForInitPromise]);
        } else {
            console.debug("Starting MSW registration");
        }

        const [register] = await Promise.all([import("@/mock-api/msw/register.ts"), preInit?.()]);
        try {
            await register.registerMsw(enabled);
        } catch (e) {
            console.error(e, "Failed to register mock service worker");
        }
    })());
}
